import {type QueryClient, useMutation, UseMutationResult, useQuery, UseQueryResult} from '@tanstack/react-query';
import {z as r} from 'zod';

import {
    fetchAndDeserialize,
    getUrlParameters,
    postAndDeserialize,
    serialize,
    serializeToURLParameters,
    type UnknownType,
} from '../../../util/api-adapter';
import {FetchMethodEnum, fetchNoContent} from '../../../util/fetch';
import {getCsrfHeaders, mainApiHeaders, rootApiUrl} from '../../api/api-const';

const basePath = '/v1/source_settings/store_accounts';

type StoreAccountCredentialsType = {
    issuerId: string;
    keyId: string;
    privateKey: string;
};

type ValidateStoreAccountRequestType = {
    catalogId: number;
    credentials: StoreAccountCredentialsType;
};

type CreateStoreAccountRequestType = ValidateStoreAccountRequestType;

export enum StoreAccountStatusEnum {
    NoError = 'no_error',
    NeedsBinding = 'needs_binding',
    PermissionError = 'permission_error',
    InvalidCredentials = 'invalid_credentials',
    UnknownError = 'unknown_error',
}

const validateStoreAccountResponseSchema = r.object({
    status: r.literal('ok'),
});

type ValidateStoreAccountResponseType = r.infer<typeof validateStoreAccountResponseSchema>;

const createStoreAccountResponseSchema = r.object({
    id: r.number(),
});

type CreateStoreAccountResponseType = r.infer<typeof createStoreAccountResponseSchema>;

const storeAccountListItemSchema = r.object({
    id: r.number(),
    catalog: r.object({
        id: r.number(),
        name: r.string(),
    }),
    status: r.nativeEnum(StoreAccountStatusEnum),
    errorText: r.string().nullable().optional(),
    login: r.string(),
    brandsCount: r.number(),
    limitedBrandsNames: r.array(
        r.object({
            id: r.number(),
            name: r.string(),
        })
    ),
});

export type StoreAccountListItemType = r.infer<typeof storeAccountListItemSchema>;

const storeAccountsListPaginatedSchema = r.object({
    results: r.array(storeAccountListItemSchema),
    next: r.string().nullable().optional(),
    previous: r.string().nullable().optional(),
    count: r.number().nullable().optional(),
    pageSize: r.number().optional(),
});

const storeAccountsListResponseSchema = r.union([
    storeAccountsListPaginatedSchema,
    r.array(storeAccountListItemSchema),
]);

type StoreAccountsListPaginatedType = r.infer<typeof storeAccountsListPaginatedSchema>;

type StoreAccountsListResponseType = r.infer<typeof storeAccountsListResponseSchema>;

type StoreAccountsListRequestType = {
    catalogId: number;
    cursor?: string | null;
    pageSize?: number;
    status?: Array<StoreAccountStatusEnum>;
};

const storeAccountAppItemSchema = r.object({
    appId: r.string(),
    appName: r.string(),
    catalogAccountId: r.number(),
    isBound: r.boolean(),
    boundBrandId: r.number().nullable(),
    boundBrandName: r.string().nullable(),
});

const storeAccountAppsListResponseSchema = r.object({
    nextCursor: r.string().nullable(),
    hasNext: r.boolean(),
    results: r.array(storeAccountAppItemSchema),
});

type StoreAccountAppsListResponseType = r.infer<typeof storeAccountAppsListResponseSchema>;

type StoreAccountAppsListRequestType = {
    cursor?: string | null;
    limit?: number;
};

type StoreAccountsCredentialsBannerRequestType = {
    catalogId?: number;
};

const storeAccountsCredentialsBannerResponseSchema = r.object({
    needToShowBanner: r.boolean(),
    items: r.array(
        r.object({
            catalogId: r.number(),
            needsCredentialsBinding: r.boolean(),
        })
    ),
});

type StoreAccountsCredentialsBannerResponseType = r.infer<typeof storeAccountsCredentialsBannerResponseSchema>;

type StoreAccountAppsBindLinkType = {
    brandId: number;
    apps: Array<string>;
};

type StoreAccountLinkAppRefType = {
    appId: string;
    appName: string;
};

/** Normalized row from `GET /store_accounts/:id/links/` (nested brand + apps). */
export type StoreAccountLinksRowType = {
    brandId: number;
    brandName: string;
    apps: Array<string>;
    linkApps: Array<StoreAccountLinkAppRefType>;
};

const storeAccountLinksAppNestedSchema = r.object({
    appId: r.string(),
    appName: r.string(),
});

const storeAccountLinksBrandNestedSchema = r.object({
    id: r.number(),
    name: r.string(),
});

const storeAccountLinksRowNestedSchema = r.object({
    brand: storeAccountLinksBrandNestedSchema,
    apps: r.array(storeAccountLinksAppNestedSchema),
});

const storeAccountLinksEnvelopeSchema = r.object({
    links: r.array(storeAccountLinksRowNestedSchema),
});

function isPlainObject(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function normalizeStoreAccountLinkRows(
    data: r.infer<typeof storeAccountLinksEnvelopeSchema>
): Array<StoreAccountLinksRowType> {
    return data.links.map((link) => {
        const linkApps = link.apps.map((app) => ({appId: app.appId, appName: app.appName}));

        return {
            brandId: link.brand.id,
            brandName: link.brand.name,
            apps: linkApps.map((app) => app.appId),
            linkApps,
        };
    });
}

function parseLegacyPrimitiveAppEntry(entry: string | number): StoreAccountLinkAppRefType {
    const id = String(entry);

    return {appId: id, appName: id};
}

function parseLegacyObjectAppEntry(record: Record<string, unknown>): StoreAccountLinkAppRefType | null {
    const appIdRaw = record.appId ?? record.app_id;
    const appNameRaw = record.appName ?? record.app_name;
    const resolvedId = typeof appIdRaw === 'string' || typeof appIdRaw === 'number' ? String(appIdRaw) : null;
    const resolvedName = typeof appNameRaw === 'string' ? appNameRaw : resolvedId;

    if (resolvedId === null || resolvedName === null) {
        return null;
    }

    return {appId: resolvedId, appName: resolvedName};
}

function parseLegacyAppEntryToLinkApp(entry: unknown): StoreAccountLinkAppRefType | null {
    if (typeof entry === 'string' || typeof entry === 'number') {
        return parseLegacyPrimitiveAppEntry(entry);
    }

    if (!isPlainObject(entry)) {
        return null;
    }

    return parseLegacyObjectAppEntry(entry);
}

function parseLegacyStoreAccountLinkRow(row: Record<string, unknown>): StoreAccountLinksRowType | null {
    const brandIdRaw = row.brandId ?? row.brand_id;
    const brandId =
        typeof brandIdRaw === 'number' ? brandIdRaw : typeof brandIdRaw === 'string' ? Number(brandIdRaw) : Number.NaN;
    const appsRaw = row.apps ?? row.appIds ?? row.app_ids;

    if (!Number.isFinite(brandId) || !Array.isArray(appsRaw)) {
        return null;
    }

    const linkApps = appsRaw.flatMap((entry) => {
        const parsed = parseLegacyAppEntryToLinkApp(entry);

        return parsed ? [parsed] : [];
    });

    if (linkApps.length === 0) {
        return null;
    }

    return {
        brandId,
        brandName: '',
        apps: linkApps.map((app) => app.appId),
        linkApps,
    };
}

function parseLegacyStoreAccountLinksResponseArray(raw: Array<unknown>): Array<StoreAccountLinksRowType> {
    return raw.flatMap((item) => {
        if (!isPlainObject(item)) {
            return [];
        }

        const parsed = parseLegacyStoreAccountLinkRow(item);

        return parsed ? [parsed] : [];
    });
}

function parseLegacyStoreAccountLinksResponse(raw: unknown): Array<StoreAccountLinksRowType> {
    if (!raw || typeof raw !== 'object') {
        return [];
    }

    if (Array.isArray(raw)) {
        return parseLegacyStoreAccountLinksResponseArray(raw);
    }

    if (!isPlainObject(raw)) {
        return [];
    }

    const nested = raw.results ?? raw.links ?? raw.data;

    if (Array.isArray(nested)) {
        return parseLegacyStoreAccountLinksResponseArray(nested);
    }

    return [];
}

function getStoreAccountLinksUrl(pk: number): string {
    return `${basePath}/${pk}/links/`;
}

async function fetchStoreAccountLinks(pk: number): Promise<Array<StoreAccountLinksRowType>> {
    const raw = await fetchAndDeserialize<UnknownType>(getStoreAccountLinksUrl(pk), r.unknown());
    const parsed = storeAccountLinksEnvelopeSchema.safeParse(raw);

    if (parsed.success) {
        return normalizeStoreAccountLinkRows(parsed.data);
    }

    return parseLegacyStoreAccountLinksResponse(raw);
}

type BindStoreAccountAppsRequestType = {
    links: Array<StoreAccountAppsBindLinkType>;
};

const bindStoreAccountAppsResponseSchema = r.object({
    changed: r.boolean(),
});

type BindStoreAccountAppsResponseType = r.infer<typeof bindStoreAccountAppsResponseSchema>;

/** store_accounts API error body used by deserializeApiError. */
export const storeAccountApiErrorBodySchema = r.object({
    status: r.string(),
    code: r.string().optional(),
});

export type StoreAccountApiErrorBodyType = r.infer<typeof storeAccountApiErrorBodySchema>;

function normalizeStoreAccountsListResponse(data: StoreAccountsListResponseType): StoreAccountsListPaginatedType {
    if (Array.isArray(data)) {
        return {results: data, count: data.length, next: null, previous: null};
    }

    return data;
}

async function validateStoreAccount(
    payload: ValidateStoreAccountRequestType
): Promise<ValidateStoreAccountResponseType> {
    return postAndDeserialize(`${basePath}/validate/`, validateStoreAccountResponseSchema, payload);
}

async function createStoreAccount(payload: CreateStoreAccountRequestType): Promise<CreateStoreAccountResponseType> {
    return postAndDeserialize(`${basePath}/`, createStoreAccountResponseSchema, payload);
}

function getStoreAccountsListUrl(options: StoreAccountsListRequestType): string {
    return `${basePath}/${getUrlParameters(options)}`;
}

function getStoreAccountAppsUrl(pk: number, options: StoreAccountAppsListRequestType = {}): string {
    const query = getUrlParameters(
        serializeToURLParameters(Object.fromEntries(Object.entries(options).filter(([, value]) => value !== null)))
    );

    return `${basePath}/${pk}/apps/${query}`;
}

async function fetchStoreAccountsList(options: StoreAccountsListRequestType): Promise<StoreAccountsListPaginatedType> {
    const raw = await fetchAndDeserialize<StoreAccountsListResponseType>(
        getStoreAccountsListUrl(options),
        storeAccountsListResponseSchema
    );

    return normalizeStoreAccountsListResponse(raw);
}

async function fetchStoreAccountApps(
    pk: number,
    options: StoreAccountAppsListRequestType = {}
): Promise<StoreAccountAppsListResponseType> {
    const url = getStoreAccountAppsUrl(pk, options);

    return fetchAndDeserialize(url, storeAccountAppsListResponseSchema);
}

async function fetchStoreAccountAppsNextPages(
    pk: number,
    options: StoreAccountAppsListRequestType,
    page: StoreAccountAppsListResponseType
): Promise<StoreAccountAppsListResponseType> {
    if (!page.hasNext || page.nextCursor === null) {
        return {...page, hasNext: false, nextCursor: null};
    }

    const nextPage = await fetchStoreAccountApps(pk, {...options, cursor: page.nextCursor});
    const restPages = await fetchStoreAccountAppsNextPages(pk, options, nextPage);

    return {
        hasNext: restPages.hasNext,
        nextCursor: restPages.nextCursor,
        results: [...page.results, ...restPages.results],
    };
}

async function fetchAllStoreAccountApps(
    pk: number,
    options: StoreAccountAppsListRequestType = {}
): Promise<StoreAccountAppsListResponseType> {
    const firstPage = await fetchStoreAccountApps(pk, options);

    return fetchStoreAccountAppsNextPages(pk, options, firstPage);
}

function getStoreAccountsCredentialsBannerUrl(options: StoreAccountsCredentialsBannerRequestType = {}): string {
    const query = getUrlParameters({catalogId: options.catalogId});

    return `${basePath}/banner/${query}`;
}

async function fetchStoreAccountsCredentialsBanner(
    options: StoreAccountsCredentialsBannerRequestType = {}
): Promise<StoreAccountsCredentialsBannerResponseType> {
    return fetchAndDeserialize(
        getStoreAccountsCredentialsBannerUrl(options),
        storeAccountsCredentialsBannerResponseSchema
    );
}

async function bindStoreAccountApps(
    pk: number,
    payload: BindStoreAccountAppsRequestType
): Promise<BindStoreAccountAppsResponseType> {
    const pathname = `${basePath}/${pk}/apps/`;
    const body = JSON.stringify(
        serialize<BindStoreAccountAppsRequestType>({
            links: payload.links,
        })
    );

    return fetchAndDeserialize<BindStoreAccountAppsResponseType>(pathname, bindStoreAccountAppsResponseSchema, {
        fetchOptions: {
            headers: {...mainApiHeaders, ...getCsrfHeaders()},
            body,
            method: FetchMethodEnum.put,
        },
    });
}

async function deleteStoreAccount(accountId: number): Promise<void> {
    const url = `${rootApiUrl}${basePath}/${accountId}/`;

    return fetchNoContent(url, {
        headers: {...mainApiHeaders, ...getCsrfHeaders()},
        method: FetchMethodEnum.delete,
    });
}

export function invalidateStoreAccountsQueries(queryClient: QueryClient): Promise<void> {
    return queryClient.invalidateQueries({
        predicate: ({queryKey}) =>
            Array.isArray(queryKey) &&
            queryKey.length > 0 &&
            typeof queryKey[0] === 'string' &&
            queryKey[0].startsWith(basePath),
    });
}

export function useValidateStoreAccountMutation(): UseMutationResult<
    ValidateStoreAccountResponseType,
    unknown,
    ValidateStoreAccountRequestType
> {
    return useMutation(validateStoreAccount);
}

export function useCreateStoreAccountMutation(): UseMutationResult<
    CreateStoreAccountResponseType,
    unknown,
    CreateStoreAccountRequestType
> {
    return useMutation(createStoreAccount);
}

export function useBindStoreAccountAppsMutation(): UseMutationResult<
    BindStoreAccountAppsResponseType,
    unknown,
    {accountId: number; payload: BindStoreAccountAppsRequestType}
> {
    return useMutation(({accountId, payload}) => bindStoreAccountApps(accountId, payload));
}

export function useDeleteStoreAccountMutation(): UseMutationResult<void, unknown, {accountId: number}> {
    return useMutation(({accountId}) => deleteStoreAccount(accountId));
}

export function useStoreAccountsList(
    options: StoreAccountsListRequestType,
    queryOptions?: {enabled?: boolean}
): UseQueryResult<StoreAccountsListPaginatedType> {
    const {catalogId} = options;

    return useQuery([getStoreAccountsListUrl(options)], () => fetchStoreAccountsList(options), {
        enabled: (queryOptions?.enabled ?? true) && catalogId >= 1,
        refetchOnWindowFocus: false,
    });
}

export function useAllStoreAccountApps(
    pk: number,
    options: Pick<StoreAccountAppsListRequestType, 'limit'> = {},
    queryOptions?: {enabled?: boolean}
): UseQueryResult<StoreAccountAppsListResponseType> {
    const {limit} = options;

    return useQuery([getStoreAccountAppsUrl(pk, {limit})], () => fetchAllStoreAccountApps(pk, {limit}), {
        enabled: (queryOptions?.enabled ?? true) && pk >= 1,
        refetchOnWindowFocus: false,
    });
}

export function useStoreAccountLinks(
    pk: number,
    queryOptions?: {enabled?: boolean; refetchOnMount?: boolean | 'always'}
): UseQueryResult<Array<StoreAccountLinksRowType>> {
    return useQuery([getStoreAccountLinksUrl(pk)], () => fetchStoreAccountLinks(pk), {
        enabled: (queryOptions?.enabled ?? true) && pk >= 1,
        refetchOnMount: queryOptions?.refetchOnMount,
        refetchOnWindowFocus: false,
        retry: false,
    });
}

export function useStoreAccountsCredentialsBanner(
    options: StoreAccountsCredentialsBannerRequestType = {},
    queryOptions?: {enabled?: boolean}
): UseQueryResult<StoreAccountsCredentialsBannerResponseType> {
    const {catalogId} = options;
    const isCatalogIdValid = !('catalogId' in options) || Number(catalogId) >= 1;

    return useQuery(
        [getStoreAccountsCredentialsBannerUrl(options)],
        () => fetchStoreAccountsCredentialsBanner(options),
        {
            enabled: (queryOptions?.enabled ?? true) && isCatalogIdValid,
            refetchOnWindowFocus: false,
            retry: false,
        }
    );
}
